import Head from 'next/head';
import React from 'react';

import { LogoutButton } from '@/auth/components/LogoutButton.component';
import {
  BookingManagementCard,
  BookingManagementPageLayout,
  useBookingManagementFeatureBlock,
} from '@/booking-management';
import { useSessionGuard } from '@/booking-management';
import { useBrandUrls, usePageTitle } from '@/brand/common/hooks';
import { useTranslation } from '@/i18n';
import { Markdown } from '@/markdown';
import { trpc } from '@/trpc/client';
import { LoadingIndicator } from '@/ui/controls';
import { Card, PageWrapper, SplitScreenLayout } from '@/ui/layout';
import { Spacer, Stack } from '@/ui/spacing';
import { useTheme } from '@/ui/theme';
import { BodySmall, Heading } from '@/ui/typography';

export default function AccountPage() {
  useBookingManagementFeatureBlock();
  useSessionGuard();

  const { t } = useTranslation('bookingManagement');
  const {
    componentProperties: { bookingManagement },
  } = useTheme();
  const pageTitle = usePageTitle(t('amendBookingRequest.title'));
  const urls = useBrandUrls();

  const { data, isLoading } = trpc.auth.session.useQuery();
  const bookings = trpc.bookingManagement.upcomingBookings.useQuery();

  const accountHasBookings =
    bookings.isFetched && bookings.data && bookings.data.length > 0;

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
      </Head>
      <BookingManagementPageLayout>
        {isLoading ? (
          <LoadingIndicator />
        ) : (
          <PageWrapper maxWidth="840px">
            <SplitScreenLayout
              descriptionSection={
                <>
                  <Heading
                    style="heading5"
                    as="h4"
                    css={{
                      ...bookingManagement?.title,
                      ['@media all and (max-width: 730px)']:
                        bookingManagement?.title?.mobile,
                    }}
                  >
                    {t('amendBookingRequest.account.title')}
                  </Heading>
                  <Spacer s="xs" />
                  <BodySmall css={bookingManagement?.description}>
                    <Markdown
                      source={t('amendBookingRequest.account.body', {
                        contactUrl: urls?.contact,
                      })}
                    />
                  </BodySmall>
                  <Spacer s="xs" />
                  <BodySmall css={bookingManagement?.description}>
                    {t('amendBookingRequest.account.bookingsListedCopy', {
                      email: data?.user?.email,
                    })}
                    <Spacer s="xxs" />
                    <LogoutButton />
                  </BodySmall>
                </>
              }
              contentSection={
                <Stack
                  css={{
                    '@media all and (max-width: 730px)':
                      bookingManagement?.bookingCards?.mobile,
                  }}
                >
                  {accountHasBookings ? (
                    bookings.data.map((booking) => (
                      <BookingManagementCard
                        key={booking.id}
                        bookingId={booking.id}
                        hotelSlug={booking.hotelReferenceId}
                        cancellable={booking.cancellable}
                      />
                    ))
                  ) : (
                    <Card>
                      <BodySmall textAlign="center">
                        {t('managementCard.noBookingsCopy')}
                      </BodySmall>
                    </Card>
                  )}
                </Stack>
              }
            />
          </PageWrapper>
        )}
      </BookingManagementPageLayout>
    </>
  );
}
